/**
 * Filters and aggregates analytics data based on date and environment.
 * @param {object} data - The raw data object from the useQuery hook.
 * @param {object} filters - The filters object from your component's state.
 * @returns {object} The filtered and aggregated data.
 */
export const getFilteredAnalytics = (data, filters) => {
  // If there's no data or no environments selected, return a default empty state
  if (!data || !filters.environment || filters.environment.length === 0) {
    return {
      daily: [],
      realtime: {
        cards: {
          countCustomers: 0,
          countInvoices: 0,
          sumInvoices: 0,
          countCoupons: 0,
        },
        graph: {
          coupons: [],
          customers: [],
          invoices: [],
          invoicesRejected: [],
        },
      },
    };
  }

  const { from: fromDate, to: toDate } = filters.date;

  const fromDateFormatted = new Date(fromDate);
  fromDateFormatted.setHours(0, 0, 0, 0);

  const toDateFormatted = new Date(toDate);
  toDateFormatted.setHours(0, 0, 0, 0);

  const dailyAggregated = {};
  const cardsAggregated = {
    countCustomers: 0,
    countInvoices: 0,
    sumInvoices: 0,
    countCoupons: 0,
  };
  const graphAggregated = {
    coupons: {},
    customers: {},
    invoices: {},
    invoicesRejected: {},
  };

  const selectedEnvironments = filters.environment; // e.g., ['online', 'offline']

  selectedEnvironments.forEach((env) => {
    // --- DAILY ---
    if (data.daily?.[env]) {
      data.daily[env]
        .filter((day) => {
          const dayDate = new Date(day.date);
          dayDate.setHours(0, 0, 0, 0);

          return dayDate >= fromDateFormatted && dayDate <= toDateFormatted;
        })
        .forEach((day) => {
          if (!dailyAggregated[day.date]) {
            dailyAggregated[day.date] = { ...day };
          } else {
            dailyAggregated[day.date].countCustomers += day.countCustomers;
            dailyAggregated[day.date].countNewCustomersParticipating +=
              day?.countNewCustomersParticipating || 0;
            dailyAggregated[day.date].countReturningCustomers +=
              day?.countReturningCustomers || 0;
            dailyAggregated[day.date].totalCustomersParticipating +=
              day?.totalCustomersParticipating || 0;
            dailyAggregated[day.date].countInvoices += day.countInvoices;
            dailyAggregated[day.date].sumInvoices += day.sumInvoices;
            dailyAggregated[day.date].countInvoicesRejected +=
              day.countInvoicesRejected;
            dailyAggregated[day.date].sumInvoicesRejected +=
              day.sumInvoicesRejected;
            dailyAggregated[day.date].countCoupons += day.countCoupons;
            dailyAggregated[day.date].valCoupons += day.valCoupons;
          }
        });
    }

    console.log('data.daily[env]', data.daily[env]);

    // --- REALTIME CARDS ---
    if (data.realtime?.cards?.[env]) {
      const card = data.realtime.cards[env];

      cardsAggregated.countCustomers += card.countCustomers || 0;
      cardsAggregated.countInvoices += card.countInvoices || 0;
      cardsAggregated.sumInvoices += card.sumInvoices || 0;
      cardsAggregated.countCoupons += card.countCoupons || 0;
    }

    // --- REALTIME GRAPH ---
    if (data.realtime?.graph?.[env]) {
      Object.keys(graphAggregated).forEach((graphType) => {
        if (!data.realtime.graph[env][graphType]) return;

        data.realtime.graph[env][graphType].forEach((point) => {
          const pointDate = new Date(point.name.split('/').reverse().join('-'));
          pointDate.setHours(0, 0, 0, 0);

          if (pointDate < fromDateFormatted || pointDate > toDateFormatted)
            return;

          if (!graphAggregated[graphType][point.name]) {
            graphAggregated[graphType][point.name] = { ...point };
          } else {
            if (graphType === 'invoices' || graphType === 'invoicesRejected') {
              graphAggregated[graphType][point.name].total += point.total;
              graphAggregated[graphType][point.name].count += point.count;
            } else {
              graphAggregated[graphType][point.name].value += point.value;
            }
          }
        });
      });
    }
  });

  // Final Data Structure
  return {
    daily: Object.values(dailyAggregated).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    ),
    realtime: {
      cards: cardsAggregated,
      graph: {
        coupons: Object.values(graphAggregated.coupons),
        customers: Object.values(graphAggregated.customers),
        invoices: Object.values(graphAggregated.invoices),
        invoicesRejected: Object.values(graphAggregated.invoicesRejected),
      },
    },
  };
};
